import apiClient from './client';

export interface QueueServicePattern {
  id: string;
  organizationId: string;
  name: string;
  description?: string | null;
  sequence: string[];
  isActive: boolean;
  autoReset: boolean;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface QueuePatternAssignment {
  id: string;
  patternId: string;
  operatorId?: string | null;
  workstationId?: string | null;
  positionPointer: number;
  autoReset: boolean;
  lastServedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface AssignmentQuery {
  operatorId?: string;
  workstationId?: string;
}

export const fetchQueuePatterns = async (includeInactive = false): Promise<QueueServicePattern[]> => {
  const response = await apiClient.get('/api/queue-patterns', { params: { includeInactive } });
  return Array.isArray(response.data?.data?.patterns) ? response.data.data.patterns : [];
};

export const fetchQueuePattern = async (id: string): Promise<QueueServicePattern | null> => {
  const response = await apiClient.get(`/api/queue-patterns/${id}`);
  return (response.data?.data?.pattern as QueueServicePattern) ?? null;
};

export interface QueuePatternPayload {
  name: string;
  description?: string | null;
  sequence: string[];
  isActive?: boolean;
  autoReset?: boolean;
}

export const createQueuePattern = async (payload: QueuePatternPayload): Promise<QueueServicePattern> => {
  const response = await apiClient.post('/api/queue-patterns', payload);
  return response.data?.data?.pattern as QueueServicePattern;
};

export const updateQueuePattern = async (
  id: string,
  payload: Partial<QueuePatternPayload>,
): Promise<QueueServicePattern> => {
  const response = await apiClient.put(`/api/queue-patterns/${id}`, payload);
  return response.data?.data?.pattern as QueueServicePattern;
};

export const deleteQueuePattern = async (id: string): Promise<void> => {
  await apiClient.delete(`/api/queue-patterns/${id}`);
};

export interface AssignQueuePatternPayload {
  operatorId?: string;
  workstationId?: string;
  autoReset?: boolean;
}

export interface AssignQueuePatternVariables {
  id: string;
  payload: AssignQueuePatternPayload;
}

export const assignQueuePattern = async ({
  id,
  payload,
}: AssignQueuePatternVariables): Promise<QueuePatternAssignment> => {
  const response = await apiClient.put(`/api/queue-patterns/${id}/assign`, payload);
  return response.data?.data?.assignment as QueuePatternAssignment;
};

export const fetchQueuePatternAssignment = async (
  query: AssignmentQuery,
): Promise<QueuePatternAssignment | null> => {
  if (!query.operatorId && !query.workstationId) {
    throw new Error('operatorId or workstationId must be provided');
  }

  const response = await apiClient.get('/api/queue-patterns/assignments', {
    params: {
      ...(query.operatorId ? { operatorId: query.operatorId } : {}),
      ...(query.workstationId ? { workstationId: query.workstationId } : {}),
    },
  });

  return (response.data?.data?.assignment as QueuePatternAssignment) ?? null;
};
